/**
 * Pricebook Detail Page
 *
 * Complete detail view for a single pricebook record with tabs
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { usePricebooks, usePricebook, usePricebookEntries, useDeletePricebook } from '../../services/queries/pricebookQueries';
import { useProducts } from '../../services/queries/productQueries';
import { persistedAccountRecords } from '../../services/mockData/accounts';
import { persistedContactRecords } from '../../services/mockData/contacts';
import { DetailPageLayout } from '../../components/detail/DetailPageLayout';
import { DetailHeader } from '../../components/detail/DetailHeader';
import { DetailTabs } from '../../components/detail/DetailTabs';
import { PricebookEntryModal } from '../../components/modals/PricebookEntryModal';
import { CreateInvoiceFromPricebookModal } from '../../components/modals/CreateInvoiceFromPricebookModal';
import { useToast } from '../../contexts/ToastContext';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import { BookOpen, Package, Users, History, Plus, Receipt } from 'lucide-react';
import { StandardButton } from '../../components/standard';
import type { Pricebook, PricebookEntry } from '../../types/index';

interface PricebookDetailPageProps {
  id: string;
  onBack: () => void;
}

export const PricebookDetailPage: React.FC<PricebookDetailPageProps> = ({ id, onBack }) => {
  const { success, error: toastError } = useToast();
  const queryClient = useQueryClient();
  const deletePricebookMutation = useDeletePricebook();
  const [activeTab, setActiveTab] = useState('overview');
  const [isEntryModalOpen, setEntryModalOpen] = useState(false);
  const [entryToEdit, setEntryToEdit] = useState<PricebookEntry | null>(null);
  const [isCreateInvoiceModalOpen, setCreateInvoiceModalOpen] = useState(false);

  const { data: pricebooks } = usePricebooks();
  const { data: pricebook, isLoading } = usePricebook(id);
  const { data: entries } = usePricebookEntries(id);
  const standardPricebookIdFromList = pricebooks?.find(pb => pb.isStandard)?.id;
  const { data: standardEntries } = usePricebookEntries(standardPricebookIdFromList ?? '');

  const { data: products } = useProducts();

  const { data: firmAccount } = useQuery({
    queryKey: ['account', pricebook?.accountId],
    queryFn: async () => {
      if (!pricebook?.accountId) return null;
      const accounts = persistedAccountRecords.get();
      return accounts.find(a => a.id === pricebook.accountId) ?? null;
    },
    enabled: !!pricebook?.accountId
  });

  const { data: partnerContact } = useQuery({
    queryKey: ['contact', pricebook?.partnerId],
    queryFn: async () => {
      if (!pricebook?.partnerId) return null;
      const contacts = persistedContactRecords.get();
      return contacts.find((c: { id: string }) => c.id === pricebook.partnerId) ?? null;
    },
    enabled: !!pricebook?.partnerId
  });

  const handleDelete = () => {
    if (!pricebook) return;
    if (pricebook.isStandard) {
      alert('Cannot delete the standard pricebook');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ${pricebook.name}?`)) return;

    deletePricebookMutation.mutate(id, {
      onSuccess: () => {
        success('Pricebook deleted', `${pricebook.name} has been deleted`);
        onBack();
      },
      onError: () => {
        toastError('Delete failed', 'Could not delete pricebook');
      },
    });
  };

  if (isLoading) {
    return (
      <DetailPageLayout onBack={onBack} backLabel="Back to Pricebooks">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </DetailPageLayout>
    );
  }

  if (!pricebook) {
    return (
      <DetailPageLayout onBack={onBack} backLabel="Back to Pricebooks">
        <div className="text-center py-12">
          <p className="text-gray-600 dark:text-gray-400">Pricebook not found</p>
        </div>
      </DetailPageLayout>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BookOpen },
    { id: 'products', label: 'Products', icon: Package },
    { id: 'clients', label: 'Client Assignments', icon: Users },
    { id: 'history', label: 'History', icon: History }
  ];

  // When firm uses default pricebook, show standard pricebook entries (read-only)
  const standardPricebookId = pricebook?.isStandard
    ? pricebook.id
    : (pricebook?.useStandardPricebook ? standardPricebookIdFromList : undefined);
  const entriesToShow = pricebook?.useStandardPricebook && !pricebook?.isStandard && standardPricebookId
    ? (standardEntries ?? [])
    : (entries ?? []);

  const entriesWithProducts = entriesToShow.map((entry: PricebookEntry) => {
    const productIdStr =
      typeof entry.productId === 'object' && entry.productId != null && typeof (entry.productId as { id?: string }).id === 'string'
        ? (entry.productId as { id: string }).id
        : String(entry.productId ?? '');
    const product = products?.find(p => p.id === productIdStr || p.id === entry.productId);
    return {
      ...entry,
      productId: productIdStr,
      productName: product?.name || 'Unknown Product',
      productCode: product?.productCode || ''
    };
  });

  const productsTabReadOnly = !!pricebook?.useStandardPricebook && !pricebook?.isStandard;

  return (
    <DetailPageLayout onBack={onBack} backLabel="Back to Pricebooks">
      <div className="max-w-6xl mx-auto space-y-6">
        <DetailHeader
          title={pricebook.name}
          subtitle={
            pricebook.isStandard
              ? 'Standard Pricebook'
              : pricebook.accountId && pricebook.useStandardPricebook
                ? 'Firm pricebook (uses standard)'
                : 'Custom Pricebook'
          }
          recordId={pricebook.id}
          status={pricebook.isActive ? 'Active' : 'Inactive'}
          statusColor={pricebook.isActive 
            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
          }
          onEdit={() => {}}
          onDelete={pricebook.isStandard ? () => {} : handleDelete}
          additionalActions={
            pricebook.accountId && shouldUseGobodo() ? (
              <StandardButton
                variant="primary"
                onClick={() => setCreateInvoiceModalOpen(true)}
                leftIcon={<Receipt className="w-4 h-4" />}
              >
                Create invoice
              </StandardButton>
            ) : undefined
          }
        />

        <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Product Count</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {entriesWithProducts.length}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Type</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {pricebook.isStandard ? 'Standard' : 'Custom'}
              </p>
            </div>
            {pricebook.accountId && (
              <>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Firm</p>
                  <Link
                    to={`/accounts/${pricebook.accountId}`}
                    className="text-lg font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {firmAccount?.name ?? pricebook.accountId}
                  </Link>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Managing Partner</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">
                    {(partnerContact as { name?: string } | null)?.name ?? '—'}
                  </p>
                </div>
              </>
            )}
          </div>
          {pricebook.accountId && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Pricing</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {pricebook.useStandardPricebook ? (
                  standardPricebookIdFromList ? (
                    <>
                      Uses standard pricebook —{' '}
                      <Link
                        to={`/pricebooks/${standardPricebookIdFromList}`}
                        className="text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        View standard pricebook
                      </Link>
                    </>
                  ) : (
                    'Uses standard pricebook'
                  )
                ) : (
                  'Custom pricebook'
                )}
              </p>
            </div>
          )}
        </div>

        <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <DetailTabs
            tabs={tabs}
            activeTab={activeTab}
            onChange={setActiveTab}
          />

          <div className="p-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Description</h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {pricebook.description || 'No description provided'}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Created Date</p>
                    <p className="text-gray-900 dark:text-white">
                      {new Date(pricebook.createdDate).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Last Modified</p>
                    <p className="text-gray-900 dark:text-white">
                      {new Date(pricebook.lastModifiedDate).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'products' && (
              <div className="space-y-4">
                {productsTabReadOnly && (
                  <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4 text-sm text-blue-800 dark:text-blue-200">
                    This firm uses the default pricebook. Products and prices below are from the standard pricebook (read-only). To add or edit entries, switch this firm to a custom pricebook.
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Products in Pricebook</h3>
                  {!productsTabReadOnly && (
                    <button
                      type="button"
                      onClick={() => {
                        setEntryToEdit(null);
                        setEntryModalOpen(true);
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Add Product
                    </button>
                  )}
                </div>
                <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-[#334155] border-b border-gray-200 dark:border-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Product</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Product Code</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Price</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Billing</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {entriesWithProducts.map((entry) => (
                        <tr key={entry.id} className="hover:bg-gray-50 dark:hover:bg-[#334155]">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            {entry.productId ? (
                              <Link
                                to={`/products/${entry.productId}`}
                                className="text-blue-600 dark:text-blue-400 hover:underline"
                              >
                                {entry.productName}
                              </Link>
                            ) : (
                              <span className="text-gray-900 dark:text-white">{entry.productName}</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {entry.productCode}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {(() => {
                              const num = Number(entry.unitPrice);
                              const valid = typeof num === 'number' && !isNaN(num);
                              const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: entry.currency || 'USD' });
                              if (entry.billingScheme === 'tiered') {
                                return valid && num > 0 ? `From ${formatter.format(num)}` : 'Tiered';
                              }
                              return valid ? formatter.format(num) : '—';
                            })()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400" title={entry.billingScheme === 'tiered' && entry.tiers?.length
                            ? entry.tiers.map(t => {
                                const upTo = t.upTo === 'inf' ? '∞' : String(t.upTo);
                                const amt = t.unitAmount != null
                                  ? new Intl.NumberFormat('en-US', { style: 'currency', currency: entry.currency }).format(t.unitAmount / 100)
                                  : t.flatAmount != null
                                    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: entry.currency }).format(t.flatAmount / 100)
                                    : '—';
                                return `Up to ${upTo}: ${amt}`;
                              }).join('; ')
                            : undefined}>
                            {entry.billingScheme === 'tiered'
                              ? `Tiered (${entry.tiersMode === 'volume' ? 'volume' : 'graduated'})`
                              : entry.recurring
                                ? `Every ${entry.recurring.intervalCount ?? 1} ${entry.recurring.interval}${(entry.recurring.intervalCount ?? 1) > 1 ? 's' : ''}`
                                : 'One-time'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              entry.isActive
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            }`}>
                              {entry.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {entriesWithProducts.length === 0 && (
                    <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                      <p>{productsTabReadOnly ? 'No products in the standard pricebook' : 'No products in this pricebook'}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'clients' && (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Client assignments will be displayed here</p>
              </div>
            )}

            {activeTab === 'history' && (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>History tracking will be available soon</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <PricebookEntryModal
        isOpen={isEntryModalOpen}
        onClose={() => {
          setEntryModalOpen(false);
          setEntryToEdit(null);
        }}
        pricebookId={id}
        entry={entryToEdit}
      />

      {pricebook.accountId && (
        <CreateInvoiceFromPricebookModal
          isOpen={isCreateInvoiceModalOpen}
          onClose={() => setCreateInvoiceModalOpen(false)}
          accountId={pricebook.accountId}
          accountName={firmAccount?.name ?? pricebook.name}
          preselectedPricebookId={pricebook.id}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['invoices'] });
            queryClient.invalidateQueries({ queryKey: ['invoiceListViewData'] });
          }}
        />
      )}
    </DetailPageLayout>
  );
};
